import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Icon, Button, LangField, NotificationEventsField,
    NotificationGroupsField, UserSelectField, SelectField,
    ClientSelectField, ImageUploader, RoutinesManager, ReplyTemplatesManager,
    MultiSelectField, LabelWithHelp, FieldHelp, ProjectSelectField,
    FormActions, DangerZone
} from '../../../components';
import type { ImageListType, IconType } from '../../../components';
import { Formik, Form, Field, FieldArray } from 'formik';
import { Avatar } from 'flowbite-react';
import type { FormikValues } from 'formik';
import { RootState, useAppDispatch, useAppSelector, setProjectAvatar, unsetProjectAvatar, loadSentryProjects, deactivateProject, activateProject } from '../../../redux';
import { Project, Change, ProjectDocumentation, ProjectLabel, SentryProject } from '../../../models';
import { v4 as uuidv4 } from 'uuid';

interface ProjectFormProps {
    isCreation: boolean,
    initialValues: FormikValues,
    onSubmit: (values: AnyObj) => void
}

type TabItem = [string, IconType];
type TabGroup = { id: string; items: TabItem[] };

function ProjectForm(props: ProjectFormProps) {
    const { isCreation, initialValues, onSubmit } = props;

    const { t }              = useTranslation();
    const dispatch           = useAppDispatch();
    const navigate           = useNavigate();
    const location           = useLocation();
    const { tab: tabParam }  = useParams();
    const { setting }        = useAppSelector((state: RootState) => state.settings);
    const { updating, archiving } = useAppSelector((state: RootState) => state.projects);
    const { sentryProjects } = useAppSelector((state: RootState) => state.sentry);

    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        if (setting?.sentry.enabled)
            dispatch(loadSentryProjects());
    }, [dispatch, setting]);

    const validate = (values: AnyObj) => {
        const errors: AnyObj = {};
        if (values.slug) {
            const slug = String(values.slug).trim().toLowerCase();
            if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug))
                errors.slug = t('projects.slug_invalid');
        }
        return errors;
    }

    const formatOptions = ProjectDocumentation.formatTypes().map((type: string) => ({
        value: type,
        label: t(`projects.documentation_format_types.${type}`)
    }));

    const sourceOptions = ProjectDocumentation.sourceTypes().map((type: string) => ({
        value: type,
        label: t(`projects.documentation_source_types.${type}`),
        disabled : type === 'local'
    }));

    const onAvatarChange = (imageList: ImageListType) => {
        if (imageList.length > 0) {
            const file: File | undefined = imageList[0].file;
            if (file)
                return dispatch(setProjectAvatar(initialValues._id, file));
        }
    };

    const removeAvatar = () => {
        return dispatch(unsetProjectAvatar(initialValues._id));
    }

    const project = new Project(initialValues);

    const groups: TabGroup[] = [
        {
            id: 'project',
            items: [
                ['general', 'info'],
                ['labels', 'title'],
                ['translations', 'notes'],
            ],
        },
        {
            id: 'repository',
            items: [
                ['repository', 'branch'],
                ['commitRules', 'commit'],
                ['tagRules', 'release'],
            ],
        },
        {
            id: 'communication',
            items: [
                ['documentation', 'comment'],
                ['notifications', 'email'],
                ['reply_templates', 'reply'],
            ],
        },
        {
            id: 'automation',
            items: [
                ['routines', 'routine'],
            ],
        },
        {
            id: 'integrations',
            items: [
                ['sentry', 'bug'],
            ],
        },
    ];

    const visibleGroups = groups
        .map((group) => ({
            ...group,
            items: group.items.filter(([label]) => (
                label !== 'sentry' || setting?.sentry.enabled
            )),
        }))
        .filter((group) => group.items.length > 0);

    const knownTabs = useMemo(
        () => groups.flatMap((group) => group.items.map(([label]) => label)),
        // groups is a stable constant structure for this component
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const projectKey = Project.navKey(project);
    const sentryEnabled = !!setting?.sentry?.enabled;

    useEffect(() => {
        if (isCreation)
            return;

        const fallback = Project.editPath(projectKey);
        if (tabParam && !knownTabs.includes(tabParam)) {
            setActiveTab('general');
            if (location.pathname !== fallback)
                navigate(fallback, { replace: true });
            return;
        }

        const next = tabParam || 'general';
        if (next === 'sentry' && setting && !sentryEnabled) {
            setActiveTab('general');
            if (location.pathname !== fallback)
                navigate(fallback, { replace: true });
            return;
        }

        setActiveTab(next);
    }, [isCreation, tabParam, knownTabs, location.pathname, navigate, projectKey, setting, sentryEnabled]);

    const selectTab = (label: string) => {
        setActiveTab(label);
        if (isCreation)
            return;
        const target = Project.editPath(projectKey, label);
        if (location.pathname !== target)
            navigate(target);
    };

    // Hide project save on tabs that manage their own persistence
    const mustHideBottomButtons = activeTab === 'routines' || activeTab === 'reply_templates';

    const sentryProjectsOptionsIds = sentryProjects?.map((p: SentryProject) => ({
        value: p.id,
        label: p.name
    }));

    const sentryProjectsOptionsSlugs = sentryProjects?.map((p: SentryProject) => ({
        value: p.slug,
        label: p.name
    }));

    const sentryPriorities = Change.getIssuesPrioritiesValues().map((p: string) => ({
        value: p,
        label: t(`projects.issuesPriorities.${p}`) || ''
    }));

    return (
        <div className="ProjectForm bg-white dark:bg-gray-800 border-b p-4 sm:p-6">
            <main className='md:flex text-center md:text-left'>
                <div id="default-sidebar" className="z-40 md:w-64">
                    <div className="p-0 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded">
                        {!isCreation && (
                            <div className="flex justify-center">
                                <ImageUploader hidePreview onChange={onAvatarChange} className="!border-0">
                                    <div className="group relative">
                                        <Avatar img={project.getAvatarUrl(80, true)} bordered size="lg" />
                                        {project.avatar && (
                                            <Button
                                                title=""
                                                icon="delete"
                                                onClick={removeAvatar}
                                                iconColor="red-600"
                                                className="hidden group-hover:block absolute -bottom-3 -right-5"
                                                color="none"
                                            />
                                        )}
                                    </div>
                                </ImageUploader>
                            </div>
                        )}
                        <div className="border-t">
                            {visibleGroups.map((group) => (
                                <div key={group.id}>
                                    <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                        {t(`projects.groups.${group.id}`)}
                                    </p>
                                    <ul className="space-y-0 font-medium text-gray-600 dark:text-gray-300">
                                        {group.items.map(([label, icon]) => (
                                            <li
                                                key={label}
                                                className={`cursor-pointer w-full text-sm py-2.5 ${(activeTab === label) ? "text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700" : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"}`}
                                                onClick={() => selectTab(label)}
                                            >
                                                <p className="ms-3 flex items-center">
                                                    <Icon color={activeTab === label ? 'gray-900' : 'gray-500'} type={icon as IconType} className="inline mr-1" />
                                                    {t(`projects.${label}`)}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className='w-full px-4 py-4'>
                    <Formik<FormikValues>
                        initialValues={{
                            ...initialValues,
                            posture: {
                                defaultLanding: initialValues?.posture?.defaultLanding === 'list' ? 'list' : 'board',
                                hiddenTabs: Array.isArray(initialValues?.posture?.hiddenTabs)
                                    ? initialValues.posture.hiddenTabs
                                    : [],
                            },
                        }}
                        enableReinitialize
                        validate={validate}
                        onSubmit={(values, { setSubmitting }) => {
                            const payload = { ...values };
                            if (payload.repository) {
                                const repository = { ...payload.repository };
                                // Empty password means "keep existing" — do not wipe server credentials.
                                if (!repository.password)
                                    delete repository.password;
                                delete repository.hasCredentials;
                                delete repository.localPath;
                                payload.repository = repository;
                            }
                            onSubmit(payload);
                        }}
                    >
                        {({ values, errors, touched, setFieldValue }) => (
                            <Form>
                                <div className="space-y-12">
                                    <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
                                        {activeTab === 'general' &&
                                        <div className="flex-1">
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                <div className="col-span-full">
                                                    <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.name')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="name"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-full">
                                                    <label htmlFor="slug" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.slug')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="slug"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            placeholder="yoda-server"
                                                        />
                                                    </div>
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('projects.slug_help')}</p>
                                                    {errors.slug && touched.slug && (
                                                        <p className="mt-1 text-xs text-red-600">{String(errors.slug)}</p>
                                                    )}
                                                </div>
                                                <div className="col-span-full">
                                                    <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.description')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            as="textarea"
                                                            name="description"
                                                            rows={3}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-full">
                                                    <label htmlFor="clients" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.clients')}
                                                    </label>
                                                    <div className="mt-2 flex flex-row">
                                                        <ClientSelectField name="clients" multiple placeholder={t('projects.select_clients') || ''} />
                                                    </div>
                                                </div>
                                                <div className="col-span-full">
                                                    <label htmlFor="linkedProjects" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.linked_projects')}
                                                    </label>
                                                    <div className="mt-2 flex flex-row">
                                                        <ProjectSelectField
                                                            name="linkedProjects"
                                                            multiple
                                                            placeholder={t('projects.select_linked_projects') || ''}
                                                            filter={(p: Project) => !initialValues?._id || p._id !== initialValues._id}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-full border-t pt-4">
                                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                        {t('projects.posture')}
                                                    </h3>
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('projects.posture_help')}</p>
                                                    <div className="mt-3 flex flex-wrap gap-2">
                                                        <button
                                                            type="button"
                                                            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                                                            onClick={() => {
                                                                setFieldValue('posture', {
                                                                    defaultLanding: 'list',
                                                                    hiddenTabs: ['releases', 'commits', 'changelog', 'analysis', 'checks'],
                                                                });
                                                            }}
                                                        >
                                                            {t('projects.posture_preset_support')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                                                            onClick={() => {
                                                                setFieldValue('posture', {
                                                                    defaultLanding: 'board',
                                                                    hiddenTabs: [],
                                                                });
                                                            }}
                                                        >
                                                            {t('projects.posture_preset_delivery')}
                                                        </button>
                                                    </div>
                                                    <div className="mt-3">
                                                        <label htmlFor="posture.defaultLanding" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                            {t('projects.default_landing')}
                                                        </label>
                                                        <div className="mt-2">
                                                            <Field
                                                                as="select"
                                                                name="posture.defaultLanding"
                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            >
                                                                <option value="board">{t('projects.kanban_view')}</option>
                                                                <option value="list">{t('projects.list_view')}</option>
                                                            </Field>
                                                        </div>
                                                    </div>
                                                    <div className="mt-4">
                                                        <p className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                            {t('projects.hidden_tabs')}
                                                        </p>
                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('projects.hidden_tabs_help')}</p>
                                                        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                                            {['releases', 'activity', 'commits', 'changelog', 'documentation', 'attachments', 'quotes', 'reporting', 'analysis', 'checks'].map((tab) => {
                                                                const selected = (values.posture?.hiddenTabs || []).includes(tab);
                                                                return (
                                                                    <label key={tab} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selected}
                                                                            onChange={() => {
                                                                                const current = values.posture?.hiddenTabs || [];
                                                                                const next = selected
                                                                                    ? current.filter((t: string) => t !== tab)
                                                                                    : [...current, tab];
                                                                                setFieldValue('posture.hiddenTabs', next);
                                                                            }}
                                                                        />
                                                                        {t(`projects.${tab === 'reporting' ? 'reporting' : tab}`)}
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                        {activeTab === 'repository' &&
                                        <div className="flex-1">
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                <div className="md:col-span-4">
                                                    <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.url')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="repository.url"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            placeholder="https://gitlab.netoxygen.ch/project/repo.git"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="md:col-span-2">
                                                    <LabelWithHelp htmlFor="branch" help={t('projects.help.branch') || ''}>
                                                        {t('projects.branch')}
                                                    </LabelWithHelp>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="repository.branch"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <h2 className="mt-4 mb-1 text-base font-semibold leadin text-sm text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                                                {t('projects.repository_auth')}
                                                <FieldHelp content={t('projects.help.repository_auth') || ''} />
                                            </h2>
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                <div className="md:col-span-4">
                                                    <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.username')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="repository.username"
                                                            autoComplete="off"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            placeholder="yoda"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.password')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="password"
                                                            name="repository.password"
                                                            autoComplete="new-password"
                                                            placeholder={
                                                                initialValues?.repository?.hasCredentials
                                                                    ? (t('projects.password_unchanged') || '••••••••')
                                                                    : undefined
                                                            }
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                        {activeTab === 'translations' &&
                                        <div className="flex-1">
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                <div className="col-span-3">
                                                    <label htmlFor="translation.languages.all" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.translation.languages.all')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <LangField
                                                            name="translation.languages.all"
                                                            multiple
                                                            placeholder={t('projects.choose_some_languages') || ''}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-3">
                                                    <label htmlFor="translation.languages.default" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.translation.languages.default')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <LangField
                                                            name="translation.languages.default"
                                                            placeholder={t('projects.choose_a_language') || ''}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 mt-3">
                                                <div className="col-span-6">
                                                    <LabelWithHelp htmlFor="translation.context" help={t('projects.help.translation_context') || ''}>
                                                        {t('projects.translation.context')}
                                                    </LabelWithHelp>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name="translation.context"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                            placeholder={t('projects.translation.context_placeholder')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                        {activeTab === 'documentation' &&
                                        <div className="flex-1">
                                            <div className="col-span-1">
                                                <LabelWithHelp htmlFor="readme.path" help={t('projects.help.readme_path') || ''}>
                                                    {t('projects.readme_path')}
                                                </LabelWithHelp>
                                                <div className="mt-2">
                                                    <Field
                                                        type="text"
                                                        name="readme.path"
                                                        className="block rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 w-full"
                                                        placeholder={t('projects.readme_path_placeholder')}
                                                    />
                                                </div>
                                            </div>
                                            <FieldArray name="documentations">
                                                {({ insert, remove, push }) => (
                                                    <div>
                                                        {values.documentations?.length > 0 && values.documentations.map((documentation: AnyObj, i: number) => (
                                                            <div key={i} className="flex flex-col border my-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900">
                                                                <div key={i} className="grid grid-cols-4 gap-x-6 gap-y-4 p-3">
                                                                    <div className='col-span-4 md:col-span-2 lg:col-span-1'>
                                                                        <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                            {t(`projects.documentation_name`)}
                                                                        </label>
                                                                        <div className="mt-2 pb-2">
                                                                            <Field
                                                                                type="text"
                                                                                name={`documentations[${i}].name`}
                                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                                                placeholder={t('projects.documentation_name_placeholder')}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-span-4 md:col-span-2 lg:col-span-1'>
                                                                        <LabelWithHelp help={t('projects.help.documentation') || ''}>
                                                                            {t(`projects.documentation_source`)}
                                                                        </LabelWithHelp>
                                                                        <div className="mt-2">
                                                                            <SelectField
                                                                                name={`documentations[${i}].source`}
                                                                                options={sourceOptions}
                                                                                placeholder={t('projects.choose_documentation_source') || ''}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-span-4 md:col-span-2 lg:col-span-1'>
                                                                        <LabelWithHelp help={t('projects.help.documentation') || ''}>
                                                                            {t(`projects.documentation_path`)}
                                                                        </LabelWithHelp>
                                                                        <div className="mt-2">
                                                                            <Field
                                                                                type="text"
                                                                                name={`documentations[${i}].path`}
                                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                                                placeholder={t('projects.documentation_path_placeholder')}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-span-4 md:col-span-2 lg:col-span-1'>
                                                                        <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                            {t(`projects.documentation_format`)}
                                                                        </label>
                                                                        <div className="mt-2">
                                                                            <SelectField
                                                                                name={`documentations[${i}].format`}
                                                                                options={formatOptions}
                                                                                placeholder={t('projects.choose_documentation_format') || ''}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="cursor-pointer flex justify-center items-centeri mb-2">
                                                                    <Button
                                                                        onClick={() => remove(i)}
                                                                        title={t('projects.remove_this_documentation')}
                                                                        color="danger"
                                                                        icon="delete"
                                                                        small
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <div className="flex justify-center">
                                                            <Button
                                                                onClick={() => push({})}
                                                                title={t('projects.add_documentation')}
                                                                color="navigate"
                                                                icon="plus"
                                                                small
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                        }
                                        {activeTab === 'labels' &&
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {t('projects.labels_desc')}
                                            </p>
                                            <FieldArray name="labels">
                                                {({ remove, push }) => (
                                                    <div className="mt-4">
                                                        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100">
                                                            {(values.labels || []).length === 0 && (
                                                                <div className="px-4 py-8 text-center text-sm italic text-gray-400">
                                                                    {t('changes.labels_empty')}
                                                                </div>
                                                            )}
                                                            {(values.labels || []).map((label: AnyObj, i: number) => {
                                                                const color = label.color || ProjectLabel.DEFAULT_COLORS[0];
                                                                const textColor = ProjectLabel.textColor(color);
                                                                return (
                                                                    <div
                                                                        key={label._id || i}
                                                                        className="flex flex-wrap items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900"
                                                                    >
                                                                        <span
                                                                            className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium min-w-[5.5rem] max-w-[9rem]"
                                                                            style={{ backgroundColor: color, color: textColor }}
                                                                            title={label.name || t('projects.label_preview') || ''}
                                                                        >
                                                                            <Icon type="tag" size={3} className="shrink-0" color="inherit" />
                                                                            <span className="truncate">
                                                                                {label.name || t('projects.label_preview')}
                                                                            </span>
                                                                        </span>

                                                                        <Field type="hidden" name={`labels[${i}]._id`} />
                                                                        <Field type="hidden" name={`labels[${i}].color`} />

                                                                        <Field
                                                                            type="text"
                                                                            name={`labels[${i}].name`}
                                                                            className="min-w-[10rem] flex-1 rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                                                                            placeholder={t('projects.label_name_placeholder')}
                                                                        />

                                                                        <div className="flex items-center gap-1.5">
                                                                            {ProjectLabel.DEFAULT_COLORS.map((c: string) => (
                                                                                <button
                                                                                    key={c}
                                                                                    type="button"
                                                                                    className={`h-4 w-4 rounded-full transition ${
                                                                                        color === c
                                                                                            ? 'ring-2 ring-offset-1 ring-gray-700 scale-110'
                                                                                            : 'opacity-70 hover:opacity-100'
                                                                                    }`}
                                                                                    style={{ backgroundColor: c }}
                                                                                    onClick={() => {
                                                                                        const next = [...(values.labels || [])];
                                                                                        next[i] = { ...next[i], color: c };
                                                                                        setFieldValue('labels', next);
                                                                                    }}
                                                                                    aria-label={c}
                                                                                />
                                                                            ))}
                                                                        </div>

                                                                        <button
                                                                            type="button"
                                                                            className="ml-auto shrink-0 rounded p-1.5 text-gray-400 hover:bg-red-500 hover:text-red-600"
                                                                            title={t('projects.remove_label') || ''}
                                                                            onClick={() => remove(i)}
                                                                        >
                                                                            <Icon type="delete" size={4} color="inherit" />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        <div className="mt-3 flex justify-start">
                                                            <Button
                                                                onClick={() => push({
                                                                    _id: uuidv4(),
                                                                    name: '',
                                                                    color: ProjectLabel.DEFAULT_COLORS[5],
                                                                })}
                                                                title={t('projects.add_label')}
                                                                color="navigate"
                                                                icon="plus"
                                                                small
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                        }
                                        {activeTab === 'notifications' &&
                                        <div className="flex-1">
                                            <p className="mt-1 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300 inline-flex items-center gap-1">
                                                {t('projects.notificationsDesc')}
                                                <FieldHelp content={t('projects.help.notification_rules') || ''} />
                                            </p>
                                            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                                                <div className="col-span-1">
                                                    <label htmlFor="notificationSettings.defaultLanguage" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t('projects.notifications_default_language')}
                                                    </label>
                                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('projects.notifications_default_language_desc')}</p>
                                                    <div className="mt-2">
                                                        <LangField
                                                            name="notificationSettings.defaultLanguage"
                                                            placeholder={t('projects.choose_a_language') || ''}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <FieldArray name="notifications">
                                                {({ insert, remove, push }) => (
                                                    <div>
                                                        {values.notifications.length > 0 && values.notifications.map((notification: AnyObj, i: number) => (
                                                            <div key={i} className="flex flex-col border my-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900">
                                                                <div key={i} className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-6 p-3">
                                                                    <div className="col-span-2">
                                                                        <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                            {t(`projects.notifications_events`)}
                                                                        </label>
                                                                        <div className="mt-2">
                                                                            <NotificationEventsField
                                                                                name={`notifications[${i}].events`}
                                                                                placeholder={t('projects.choose_notification_events') || ''}
                                                                                multiple
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-span-2">
                                                                        <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                            {t(`projects.notifications_groups`)}
                                                                        </label>
                                                                        <div className="mt-2">
                                                                            <NotificationGroupsField
                                                                                name={`notifications[${i}].groups`}
                                                                                placeholder={t('projects.choose_notification_groups') || ''}
                                                                                multiple
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-span-2">
                                                                        <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                            {t(`projects.notifications_users`)}
                                                                        </label>
                                                                        <div className="mt-2">
                                                                            <UserSelectField
                                                                                name={`notifications[${i}].users`}
                                                                                placeholder={t('projects.choose_notification_users') || ''}
                                                                                multiple
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="cursor-pointer flex justify-center items-centeri mb-2">
                                                                    <Button
                                                                        onClick={() => remove(i)}
                                                                        title={t('projects.remove_this_notification_rule')}
                                                                        color="danger"
                                                                        icon="delete"
                                                                        small
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <div className="flex justify-center">
                                                            <Button
                                                                onClick={() => push({})}
                                                                title={t('projects.add_notification_rule')}
                                                                color="navigate"
                                                                icon="plus"
                                                                small
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                        }
                                        {activeTab === 'routines' &&
                                        <div className="flex-1">
                                            <p className="mb-2 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300">
                                                {t('projects.routinesDesc')}
                                            </p>
                                            <RoutinesManager projectId={values._id} />
                                        </div>
                                        }
                                        {activeTab === 'reply_templates' &&
                                        <div className="flex-1">
                                            <ReplyTemplatesManager
                                                projectId={values._id}
                                                projectName={values.name}
                                            />
                                        </div>
                                        }
                                        {activeTab === 'commitRules' &&
                                        <div className="flex-1">
                                            <p className="mt-1 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300 inline-flex items-center gap-1">
                                                {t('projects.commitRulesDesc')}
                                                <FieldHelp content={t('projects.help.commit_rules') || ''} />
                                            </p>
                                            <div className="grid grid-cols-3 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                {['feature', 'fix', 'release', 'i18n', 'ui', 'refactor', 'test', 'doc', 'lint', 'build', 'dependencies', 'chore'].map(key => (
                                                <div key={key} className="col-span-2">
                                                    <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t(`projects.${key}`)}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name={`commitRules.${key}`}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                                ))}
                                            </div>
                                        </div>
                                        }
                                        {activeTab === 'tagRules' &&
                                        <div className="flex-1">
                                            <p className="mt-1 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300 inline-flex items-center gap-1">
                                                {t('projects.tagRulesDesc')}
                                                <FieldHelp content={t('projects.help.commit_rules') || ''} />
                                            </p>
                                            <div className="grid grid-cols-3 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                {['releaseTag'].map(key => (
                                                <div key={key} className="col-span-6">
                                                    <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t(`projects.${key}`)}
                                                    </label>
                                                    <div className="mt-2">
                                                        <Field
                                                            type="text"
                                                            name={`tagRules.${key}`}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                                ))}
                                            </div>
                                        </div>
                                        }
                                        {activeTab === 'sentry' &&
                                        <div className="flex-1">
                                            <p className="mt-1 text-sm leading-6 font-normal text-gray-600 dark:text-gray-300">
                                                {t('projects.sentryDesc')}
                                            </p>
                                            <div className="grid grid-cols-6 gap-x-6 gap-y-4 lg:grid-cols-12">
                                                <div className="col-span-6 mt-2">
                                                    <label htmlFor="sentry.projects" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                        {t(`projects.issuesSources`)}
                                                    </label>
                                                    <div>
                                                        <small>{t(`projects.issuesSourcesDesc`)}</small>
                                                        <MultiSelectField name="sentry.issues.sources" options={sentryProjectsOptionsIds}
                                                            placeholder={t('projects.choose_some_sentry_projects') || ''} />
                                                    </div>
                                                </div>
                                                <div className="col-span-6 mt-2">
                                                    <LabelWithHelp htmlFor="sentry.priority" help={t('projects.help.sentry_priority') || ''}>
                                                        {t(`projects.issuesPriority`)}
                                                    </LabelWithHelp>
                                                    <div>
                                                        <small>{t(`projects.issuesPriorityDesc`)}</small>
                                                        <SelectField name="sentry.issues.priority" options={sentryPriorities}
                                                            placeholder={t('projects.choose_an_issue_priority') || ''} />
                                                    </div>
                                                </div>
                                                <div className="col-span-6 mt-2">
                                                    <LabelWithHelp htmlFor="sentry.exclude_pattern" help={t('projects.help.sentry_exclude') || ''}>
                                                        {t(`projects.excludePattern`)}
                                                    </LabelWithHelp>
                                                    <div>
                                                        <small>{t(`projects.excludePatternDesc`)}</small>
                                                        <Field
                                                            type="text"
                                                            name="sentry.issues.exclude_pattern"
                                                            placeholder={t('projects.excludePatternPlaceholder') || ''}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-x-6 gap-y-4 sm:grid-cols-6">
                                                <div className="col-span-12 mt-4">
                                                    <LabelWithHelp htmlFor="sentry.projects" help={t('projects.help.releases_targets') || ''}>
                                                        {t(`projects.releasesTargets`)}
                                                    </LabelWithHelp>
                                                    <div>
                                                        <small>{t(`projects.releasesTargetsDesc`)}</small>
                                                        <MultiSelectField name="sentry.releases.targets" options={sentryProjectsOptionsSlugs}
                                                            placeholder={t('projects.choose_some_sentry_projects') || ''} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </div>
                                </div>

                                {!mustHideBottomButtons && (
                                    <FormActions
                                        cancelTo={isCreation ? '/projects' : Project.path(project)}
                                        loading={updating === 'pending'}
                                    />
                                )}
                            </Form>
                        )}
                    </Formik>
                    {!isCreation && activeTab === 'general' && (
                        <DangerZone
                            deleteTo={`/projects/delete/${Project.navKey(project)}`}
                            description={t('projects.danger_zone_hint') as string}
                            archived={project.active === false}
                            archiveLoading={archiving === 'pending'}
                            onArchive={() => dispatch(deactivateProject(project._id))}
                            onUnarchive={() => dispatch(activateProject(project._id))}
                        />
                    )}
                </div>
            </main>
        </div>
    );

}

export default ProjectForm;
